import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "../../ui/dialog";
import { Button } from "../../ui/button";
import { Checkbox } from "../../ui/checkbox";
import { Badge } from "../../ui/badge";
import { Loader2, ShieldCheck, CheckCircle } from "lucide-react";
import en from "../../../locales/en";
import cs from "../../../locales/cs";

// Convert numeric SAIL to Roman numeral
const sailToRoman = (sail) => {
  const map = { 1: "I", 2: "II", 3: "III", 4: "IV", 5: "V", 6: "VI" };
  return map[sail] || sail;
};

/**
 * LUCVerificationModal - Modal for LUC scope verification before self-authorization
 * 
 * Shows two-column comparison:
 * - LEFT: What the LUC provides
 * - RIGHT: What the mission requires
 * 
 * Requires explicit checkbox confirmation before allowing self-authorization.
 */
export const LUCVerificationModal = ({
  open,
  onClose,
  onConfirm,
  luc,
  mission,
  missionSailLevel,
  missionDroneClass,
  language = "cs",
}) => {
  const t = language === "cs" ? cs.checkmate.lucVerification : en.checkmate.lucVerification;
  const [confirmed, setConfirmed] = useState(false);
  const [loading, setLoading] = useState(false);
  
  // Extract LUC scope details
  const lucScope = luc?.details?.authorization_scope || luc?.scope || {};
  const lucMaxSail = lucScope.max_sail_level || lucScope.maxSailLevel;
  const lucDroneClasses = lucScope.drone_classes || lucScope.droneClasses || [];
  const lucPrivileges = lucScope.privileges || [];
  
  // Format privileges for display
  const formatPrivilege = (priv) => {
    if (!priv) return priv;
    return priv
      .replace(/_/g, " ")
      .replace(/sail/gi, "SAIL")
      .split(" ")
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(" ");
  };
  
  const handleConfirm = async () => {
    if (!confirmed) return;
    
    setLoading(true);
    try {
      await onConfirm();
      setConfirmed(false);
    } catch (err) {
      // Error handling done by parent
    } finally {
      setLoading(false);
    }
  };
  
  const handleClose = () => {
    setConfirmed(false);
    onClose();
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[600px]" data-testid="luc-verification-modal">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className="p-2 rounded-lg bg-purple-100 dark:bg-purple-900/40">
              <ShieldCheck className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <DialogTitle className="text-lg">{t.title}</DialogTitle>
              <DialogDescription className="text-sm">{t.subtitle}</DialogDescription>
            </div>
          </div>
        </DialogHeader>
        
        {/* Two-column comparison */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 py-4">
          {/* LEFT: LUC Provides */}
          <div className="p-4 rounded-lg bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800">
            <h4 className="text-sm font-semibold text-emerald-800 dark:text-emerald-200 mb-3">
              {t.provides}
            </h4>
            
            <div className="space-y-3">
              {/* Max SAIL Level */}
              <div>
                <label className="text-xs text-emerald-600 dark:text-emerald-400">{t.fields.maxSailLevel}</label>
                <p className="text-base font-semibold text-emerald-900 dark:text-emerald-100">
                  {lucMaxSail ? `SAIL ${sailToRoman(lucMaxSail)}` : "-"}
                </p>
              </div>
              
              {/* Drone Classes */}
              <div>
                <label className="text-xs text-emerald-600 dark:text-emerald-400">{t.fields.droneClasses}</label>
                <div className="flex flex-wrap gap-1 mt-1">
                  {lucDroneClasses.length > 0 ? (
                    lucDroneClasses.map((cls) => (
                      <Badge key={cls} variant="outline" className="text-xs bg-emerald-100 dark:bg-emerald-800/40">
                        {cls}
                      </Badge>
                    ))
                  ) : (
                    <span className="text-sm text-emerald-700 dark:text-emerald-300">-</span>
                  )}
                </div>
              </div>
              
              {/* Privileges */}
              <div>
                <label className="text-xs text-emerald-600 dark:text-emerald-400">{t.fields.privileges}</label>
                <ul className="mt-1 space-y-1">
                  {lucPrivileges.length > 0 ? (
                    lucPrivileges.map((priv, idx) => (
                      <li key={idx} className="text-xs text-emerald-700 dark:text-emerald-300 flex items-start gap-1">
                        <CheckCircle className="h-3 w-3 mt-0.5 flex-shrink-0" />
                        <span>{formatPrivilege(priv)}</span>
                      </li>
                    ))
                  ) : (
                    <li className="text-sm text-emerald-700 dark:text-emerald-300">-</li>
                  )}
                </ul>
              </div>
            </div>
          </div>
          
          {/* RIGHT: Mission Requires */}
          <div className="p-4 rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800">
            <h4 className="text-sm font-semibold text-blue-800 dark:text-blue-200 mb-3">
              {t.requires}
            </h4>
            
            <div className="space-y-3">
              {/* Mission SAIL */}
              <div>
                <label className="text-xs text-blue-600 dark:text-blue-400">{t.fields.missionSail}</label>
                <p className="text-base font-semibold text-blue-900 dark:text-blue-100">
                  {missionSailLevel ? `SAIL ${typeof missionSailLevel === 'number' ? sailToRoman(missionSailLevel) : missionSailLevel}` : "-"}
                </p>
              </div>
              
              {/* Mission Drone Class */}
              <div>
                <label className="text-xs text-blue-600 dark:text-blue-400">{t.fields.missionDroneClass}</label>
                <p className="text-base font-semibold text-blue-900 dark:text-blue-100">
                  {missionDroneClass || mission?.drone_class || mission?.ua_class || "-"}
                </p>
              </div>
              
              {/* Operation Type */}
              <div>
                <label className="text-xs text-blue-600 dark:text-blue-400">Operation Type</label>
                <p className="text-base font-semibold text-blue-900 dark:text-blue-100">
                  {mission?.operation_type || "-"}
                </p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Verdict line */}
        <div className="py-2 px-4 rounded-lg bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800">
          <p className="text-sm text-green-700 dark:text-green-300 flex items-center gap-2">
            <CheckCircle className="h-4 w-4" />
            {t.appearsToCover}
          </p>
        </div>
        
        {/* Mandatory checkbox */}
        <div className="flex items-start gap-3 py-4">
          <Checkbox
            id="luc-confirm"
            checked={confirmed}
            onCheckedChange={setConfirmed}
            className="mt-1"
            data-testid="luc-scope-checkbox"
          />
          <label 
            htmlFor="luc-confirm" 
            className="text-sm text-[var(--text-secondary)] cursor-pointer leading-relaxed"
          >
            {t.confirmCheckbox}
          </label>
        </div>
        
        <DialogFooter className="gap-2">
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            {t.cancelBtn}
          </Button>
          <Button
            onClick={handleConfirm}
            disabled={!confirmed || loading}
            className="bg-purple-600 hover:bg-purple-700 gap-2"
            data-testid="luc-self-authorize-btn"
          >
            {loading ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                {language === "cs" ? "Zpracovávám..." : "Processing..."}
              </>
            ) : (
              <>
                <ShieldCheck className="h-4 w-4" />
                {t.selfAuthorizeBtn}
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LUCVerificationModal;
